<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { apiGet, apiPut } from "../api";

interface Category { id: string; slug: string; name: string; live: boolean; sortOrder: number; swatch?: string; productCount: number }

const router = useRouter();
const items = ref<Category[]>([]);
const loading = ref(true);
const error = ref("");
const liveCount = computed(() => items.value.filter((c) => c.live).length);

async function load() {
  try {
    const d = await apiGet<{ items: Category[] }>("/categories");
    items.value = d.items.sort((a, b) => a.sortOrder - b.sortOrder);
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
async function toggle(c: Category) {
  const next = !c.live;
  c.live = next; // optimistisch
  try {
    await apiPut(`/categories/${c.id}`, { name: c.name, slug: c.slug, swatch: c.swatch, sortOrder: c.sortOrder, live: next });
  } catch (e) {
    c.live = !next;
    error.value = `Wijzigen mislukt: ${(e as Error).message}`;
  }
}
onMounted(load);
</script>

<template>
  <div>
    <div class="phead">
      <div>
        <h1 class="title">Categorieën <span class="count">· {{ liveCount }} van {{ items.length }} live</span></h1>
        <p class="info">Live = zichtbaar in de winkel (Ontdek-stories &amp; navigatie).</p>
      </div>
      <RouterLink to="/categorieen/nieuw" class="newbtn">+ Nieuwe categorie</RouterLink>
    </div>

    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <table v-else class="tbl">
      <thead>
        <tr><th>Categorie</th><th>Producten</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        <tr v-for="c in items" :key="c.id" class="clickrow" @click="router.push(`/categorieen/${c.id}/bewerken`)">
          <td>
            <div class="cat">
              <span class="sw" :style="{ background: c.swatch || 'var(--img-placeholder)' }" />
              <div><strong>{{ c.name }}</strong><div class="sub">{{ c.slug }}</div></div>
            </div>
          </td>
          <td>{{ c.productCount }}</td>
          <td><span class="status" :class="{ on: c.live }">{{ c.live ? 'Live' : 'Verborgen' }}</span></td>
          <td class="right" @click.stop>
            <button class="toggle" :class="{ on: c.live }" :aria-pressed="c.live" @click="toggle(c)">
              <span class="knob" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.phead { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 6px; }
.count { color: var(--muted); font-weight: 600; }
.info { color: var(--mid); font-size: 13px; margin: 0; }
.newbtn { flex: 0 0 auto; background: var(--coral); color: #fff; text-decoration: none; border-radius: 999px; padding: 9px 16px; font-weight: 700; font-size: 14px; }
.muted { color: var(--muted); }
.err { color: var(--red); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 14px 12px; border-bottom: 1px solid var(--hairline); }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.cat { display: flex; align-items: center; gap: 12px; }
.sw { width: 22px; height: 22px; border-radius: 999px; border: 1px solid rgba(0,0,0,.08); flex: 0 0 auto; }
.sub { color: var(--muted); font-size: 12px; }
.status { font-size: 13px; color: var(--muted); }
.status.on { color: var(--green); }
.right { text-align: right; }
.toggle { width: 46px; height: 26px; border-radius: 999px; border: 0; background: var(--border-input); position: relative; cursor: pointer; transition: background 0.15s; }
.toggle.on { background: var(--green); }
.knob { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 999px; background: #fff; transition: left 0.15s; }
.toggle.on .knob { left: 23px; }
</style>
